<template>
  <div class="tw-flex tw-flex-col tw-h-full">
    <FancyServerTable
      v-if="isStateLoaded"
      ref="fancyTableRef"
      :data="sepaPaymentsTableData"
      :columns="sepaPaymentsTableHeaders"
      :enable-filters="true"
      :enable-pagination="true"
      :enable-column-visibility="true"
      :enable-column-pinning="true"
      :initial-column-pinning="
        currentSavedState?.columnPinning || {
          left: [],
          right: ['actions'],
        }
      "
      :initial-column-visibility="currentSavedState?.columnVisibility || {}"
      :initial-column-order="currentSavedState?.columnOrder || []"
      :initial-column-sizing="currentSavedState?.columnSizing || {}"
      :initial-column-filters="currentSavedState?.columnFilters || []"
      :initial-sorting="currentSavedState?.sorting ?? []"
      :total-rows="totalRows"
      :page-index="pageIndex"
      :page-size="pageSize"
      :auto-filter="true"
      :loading="isLoading"
      @update:state="debouncedHandleTableStateChange"
      @fetch-data="fetchSepaPayments"
    >
      <template #config-manager>
        <FancyTableConfigManager
          :saved-configs="savedTableConfigs"
          :current-config="currentSavedState"
          :is-loading="false"
          :can-save-state="canSaveState"
          @save-config="handleSaveConfig"
          @update-config="handleUpdateConfig"
          @apply-config="handleApplyConfig"
          @delete-config="handleDeleteConfig"
          @mark-as-default="handleMarkAsDefault"
        />
      </template>
    </FancyServerTable>
  </div>
</template>

<script setup lang="ts">
import { h, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSepaStore } from '@/stores/sepa.store';
import {
  FancyServerTable,
  type FancyTableColumnDef,
  type ServerTableOptions,
  useTableRenderers,
  FieldType,
  FilterType,
  FancyTableConfigManager,
} from '@/components/shared/fancyTables';
import { GetPaymentNumbersRenderer } from './Renderes';
import { printFormat } from 'iban-ts';
import SepaPaymentsTableRowActions from './SepaPaymentsTableRowActions.vue';
import { useFancyTableConfig } from '@/_helpers/composables/fancy-table-config.composable';

const fancyTableRef = ref<any>(null);
const { GetFieldRenderer } = useTableRenderers();

const { getSepaPayments } = useSepaStore();
const { t } = useI18n();

const props = defineProps<{
  customerId: number;
}>();

// Map filters to API params
const mapFiltersToApiParams = (filters: any[]) => {
  const params: any = {};

  filters.forEach(filter => {
    const { id, value } = filter;

    switch (id) {
      case 'owner_name':
        if (value) params.owner_name = value;
        break;
      case 'source_iban':
        if (value) params.iban = value;
        break;
      case 'created_on':
        if (value?.from) params.created_after = value.from;
        if (value?.to) params.created_before = value.to;
        break;
      case 'amount':
        if (value?.start !== undefined) params.amount_gte = value.start;
        if (value?.end !== undefined) params.amount_lte = value.end;
        break;
      case 'payment_numbers':
        if (value) params.payment_number = value;
        break;
      default:
        break;
    }
  });

  return params;
};

// Map sorting to API param
const mapSortingToApiParam = (sorting: any[]): string => {
  if (sorting.length === 0) return '';

  const sort = sorting[0];
  const { id, desc } = sort;
  let backendField = id;

  switch (id) {
    case 'owner_name':
      backendField = 'owner_name_sort';
      break;
    case 'source_iban':
      backendField = 'source_iban_sort';
      break;
    case 'created_on':
      backendField = 'created_on';
      break;
    case 'amount':
      backendField = 'amount_sort';
      break;
    case 'payment_numbers':
      // Payment numbers is a many-to-many field, not sortable
      return '';
    default:
      backendField = id;
  }

  return desc ? `-${backendField}` : backendField;
};

// Server-side fetch function
const fetchSepaPayments = async (options?: ServerTableOptions) => {
  isLoading.value = true;

  try {
    const currentPageIndex = options?.pageIndex ?? 0;
    const currentPageSize = options?.pageSize ?? 10;
    const filters = options?.filters ?? [];
    const sorting = options?.sorting ?? [];

    // Update pagination state
    pageIndex.value = currentPageIndex;
    pageSize.value = currentPageSize;

    // Map filters to API params
    const filterParams = mapFiltersToApiParams(filters);

    // Map sorting to API param
    const orderingParam = mapSortingToApiParam(sorting);

    const apiParams = {
      page: currentPageIndex + 1,
      page_size: currentPageSize,
      customer: props.customerId,
      ...filterParams,
      ...(orderingParam && { ordering: orderingParam }),
    };

    const response = await getSepaPayments(apiParams);

    sepaPaymentsTableData.value = response.results;
    totalRows.value = response.count;
  } catch (error) {
    console.error('Error fetching SEPA payments:', error);
    sepaPaymentsTableData.value = [];
    totalRows.value = 0;
  } finally {
    isLoading.value = false;
  }
};

const getSepaPaymentsTableList = async () => {
  // This is now a wrapper for fetchSepaPayments for backward compatibility
  await fetchSepaPayments();
};

// Initialize the table config composable
const {
  savedTableConfigs,
  currentSavedState,
  canSaveState,
  debouncedHandleTableStateChange,
  handleSaveConfig,
  handleApplyConfig,
  handleUpdateConfig,
  handleDeleteConfig,
  handleMarkAsDefault,
  isStateLoaded,
} = useFancyTableConfig({
  tableName: 'sepa_payments',
  fancyTableRef,
  tableLoader: getSepaPaymentsTableList,
});

const isLoading = ref(false);
const sepaPaymentsTableData = ref<any[]>([]);
const totalRows = ref(0);
const pageIndex = ref(0);
const pageSize = ref(10);

const sepaPaymentsTableHeaders: FancyTableColumnDef<any>[] = [
  {
    header: t('sepa_payments.titles.account_owner'),
    accessorKey: 'owner_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 200,
  },
  {
    header: t('sepa_payments.titles.iban'),
    accessorKey: 'source_iban',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    filterFn: 'iban',
    cell: (info: any) => {
      return printFormat(info.getValue() as string);
    },
    size: 350,
  },
  {
    header: t('sepa_payments.titles.creation_date'),
    enableSorting: true,
    accessorKey: 'created_on',
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.DATE,
    filterType: FilterType.DATE_RANGE,
    cell: GetFieldRenderer(FieldType.DATE),
    filterFn: 'dateRange',
    size: 200,
  },
  {
    header: t('sepa_payments.titles.total_payed_amount'),
    accessorKey: 'amount',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.MONEY,
    filterType: FilterType.NUMBER_RANGE,
    filterFn: 'inNumberRange',
    sortingFn: 'money',
    cell: GetFieldRenderer(FieldType.MONEY),
    size: 200,
  },
  {
    header: t('sepa_payments.titles.assigned_payment_no'),
    accessorKey: 'payment_numbers',
    enableSorting: false,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.ARRAY,
    cell: GetPaymentNumbersRenderer(),
    filterType: FilterType.TEXT,
    filterFn: 'arrayIncludesPartial',
    size: 200,
  },
  {
    header: t('common.headers.actions'),
    accessorKey: 'actions',
    enableSorting: false,
    enableColumnFilter: false,
    enableHiding: false,
    hideFromColumnConfig: true,
    cell: info => {
      return h(SepaPaymentsTableRowActions, {
        sepaPayments: info.row.original,
        onReloadTable: getSepaPaymentsTableList,
      });
    },
  },
];

onMounted(async () => {
  await getSepaPaymentsTableList();
});
</script>
